export const MAX_ASK_ATTACHMENTS = 5;
export const ASK_ATTACHMENTS_COLUMNS = 3;

/**
 * Media CDN origin that fronts the storage bucket (S3 + CloudFront). Matches the
 * host the backend presigns Ask AI attachments against, so a historical
 * attachment — which only exposes its ``s3_key`` — can be rendered without a
 * backend round-trip.
 */
export const MEDIA_CDN_ORIGIN = "https://d2ox6kgfo0vgc9.cloudfront.net";

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico"]);

const SUPPORTED_ATTACHMENT_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "pdf",
  "docx",
]);
const SUPPORTED_ATTACHMENT_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

export const UNSUPPORTED_ATTACHMENT_MESSAGE =
  "Unsupported file type. Attach a PNG, JPG, GIF, WEBP, PDF or DOCX file.";

export function extensionFromAttachment(attachment) {
  const fromProp = (attachment?.extension || "").replace(/^\./, "").toLowerCase();
  if (fromProp) {
    return fromProp;
  }
  const match = /\.([a-zA-Z0-9]+)$/.exec(attachment?.name || "");
  return match ? match[1].toLowerCase() : "";
}

export function badgeLabelFromExtension(extension) {
  return (extension || "FILE").toUpperCase();
}

export function isImageAttachment(attachment) {
  const ext = extensionFromAttachment(attachment);
  if (IMAGE_EXTENSIONS.has(ext)) {
    return true;
  }
  const type = attachment?.file?.type || "";
  return typeof type === "string" && type.startsWith("image/");
}

export function isPdfAttachment(attachment) {
  const ext = extensionFromAttachment(attachment);
  if (ext === "pdf") {
    return true;
  }
  const type = attachment?.file?.type || "";
  return type === "application/pdf";
}

export function isDocumentAttachment(attachment) {
  const ext = extensionFromAttachment(attachment);
  return ext === "doc" || ext === "docx";
}

export function truncateAttachmentName(name, maxLength = 22) {
  const value = String(name || "").trim();
  if (value.length <= maxLength) {
    return value;
  }
  const head = Math.max(8, Math.floor(maxLength * 0.45));
  const tail = Math.max(4, maxLength - head - 3);
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
}

export function attachmentFromFile(file) {
  const name = file?.name || "attachment";
  const extension = badgeLabelFromExtension(extensionFromAttachment({ name }));
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    extension,
    file,
  };
}

/**
 * Resolve the URL a preview should load.
 *
 * Prefers an explicit ``previewUrl``/``url`` (upload responses and the Django
 * history serializer both expose one) and otherwise derives it from the storage
 * ``s3_key`` against the media CDN origin, so IQ-microservice history — which
 * persists only the key — still renders.
 */
export function attachmentPreviewUrl(attachment) {
  const explicit = attachment?.previewUrl || attachment?.url;
  if (typeof explicit === "string" && explicit.trim()) {
    return explicit.trim();
  }
  const key = attachment?.s3_key || attachment?.s3Key;
  if (typeof key === "string" && key.trim()) {
    return `${MEDIA_CDN_ORIGIN}/${encodeURI(key.replace(/^\/+/, "").trim())}`;
  }
  return "";
}

/**
 * Map an upload/history attachment reference to ``AskAQuestionAttachmentCard``
 * props: ``{ id, name, previewUrl, extension }``.
 */
export function attachmentCardFromRef(attachment) {
  if (!attachment || typeof attachment !== "object") {
    return null;
  }
  const name = attachment.file_name || attachment.name || "attachment";
  const card = {
    id: attachment.id,
    name,
    previewUrl: attachmentPreviewUrl(attachment),
  };
  const extension = extensionFromAttachment(card);
  if (extension) {
    card.extension = extension.toUpperCase();
  }
  return card;
}

/**
 * Rule 12: only images (PNG, JPG, GIF, WEBP) and documents (PDF, DOCX) can be
 * attached. The extension decides when the file has one; the MIME type covers
 * extension-less files such as clipboard images.
 */
export function isSupportedAttachmentFile(file) {
  const extension = extensionFromAttachment({ name: file?.name });
  if (extension) {
    return SUPPORTED_ATTACHMENT_EXTENSIONS.has(extension);
  }
  return SUPPORTED_ATTACHMENT_MIME_TYPES.has(String(file?.type || "").toLowerCase());
}
